import { useState } from 'react';
import { createPersonalMemberCardBodySchema } from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import CardBackgroundControl from './CardBackgroundControl';
import CardBalanceForm from './CardBalanceForm';
import { usePersonalMemberCard } from '@/hooks-v2/aggregator/usePersonalMemberCard';
import type {
  PersonalMemberCardSectionProps,
  PersonalCardDraft,
  PersonalCardFeedback,
} from '@/types/personal-member-card.types';

export default function PersonalMemberCardSection(
  props: PersonalMemberCardSectionProps,
) {
  const { t } = useTranslation();
  const card = usePersonalMemberCard(props);
  const [draft, setDraft] = useState<PersonalCardDraft | null>(null);
  const [feedback, setFeedback] = useState<PersonalCardFeedback>(null);
  const [saving, setSaving] = useState(false);
  const busy = saving || card.isSaving;
  const submit = async () => {
    if (busy || card.card === undefined || card.error !== null) return;
    const parsed = createPersonalMemberCardBodySchema.safeParse({
      member_id: draft?.memberId ?? '',
    });
    if (!parsed.success) {
      setFeedback('invalid');
      return;
    }
    if (draft === null) return;
    setSaving(true);
    setFeedback(null);
    try {
      await card.save({ ...draft, memberId: parsed.data.member_id });
      setDraft(null);
      setFeedback('success');
    } catch (error: unknown) {
      setFeedback(
        typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 'CONFLICT'
          ? 'conflict'
          : 'error',
      );
    } finally {
      setSaving(false);
    }
  };
  const refresh = async () => {
    const result = await card.refetch();
    if (result.error === null) {
      setDraft(null);
      setFeedback(null);
    }
  };
  // The existing details remain usable for ordinary staff without an admin-only request prompt.
  if (card.error?.code === 'FORBIDDEN') return null;
  return (
    <section
      className="mb-8 rounded-lg border border-gray-200 p-4"
      aria-labelledby="personal-member-card-heading"
    >
      <h3
        id="personal-member-card-heading"
        className="mb-3 text-lg font-semibold"
      >
        {t('merchant.analytics.personalCard.title')}
      </h3>
      {card.isLoading || (card.card === undefined && card.error === null) ? (
        <p role="status">{t('common.loading')}</p>
      ) : card.error !== null ? (
        <div>
          <p role="alert">{t('merchant.analytics.personalCard.loadFailed')}</p>
          <button type="button" onClick={() => void refresh()}>
            {t('merchant.analytics.personalCard.retry')}
          </button>
        </div>
      ) : (
        <>
          {card.card !== null && card.card !== undefined && (
            <p
              className="mb-3 whitespace-pre-wrap break-all font-mono"
              data-testid="personal-card-value"
            >
              {card.card.member_id}
            </p>
          )}
          {draft !== null || card.card === null ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <label
                htmlFor="personal-member-id"
                className="block text-sm font-medium"
              >
                {t('merchant.analytics.personalCard.memberId')}
              </label>
              <input
                id="personal-member-id"
                type="text"
                value={draft?.memberId ?? ''}
                disabled={busy}
                onChange={(event) => {
                  setDraft({
                    memberId: event.target.value,
                    expectedRevision:
                      draft !== null
                        ? draft.expectedRevision
                        : (card.card?.revision ?? null),
                  });
                  setFeedback(null);
                }}
                aria-describedby="personal-member-id-help"
                aria-invalid={feedback === 'invalid'}
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2"
              />
              <p
                id="personal-member-id-help"
                className="my-2 text-sm text-gray-600"
              >
                {t('merchant.analytics.personalCard.memberIdHelp')}
              </p>
              <button
                type="submit"
                disabled={busy}
                className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
              >
                {busy ? t('common.saving') : t('common.save')}
              </button>
              {draft !== null && card.card !== null && (
                <button
                  type="button"
                  disabled={busy}
                  className="ml-3"
                  onClick={() => {
                    setDraft(null);
                    setFeedback(null);
                  }}
                >
                  {t('common.cancel')}
                </button>
              )}
            </form>
          ) : (
            <button
              type="button"
              disabled={busy}
              className="text-blue-600"
              onClick={() => {
                if (card.card)
                  setDraft({
                    memberId: card.card.member_id,
                    expectedRevision: card.card.revision,
                  });
                setFeedback(null);
              }}
            >
              {t('merchant.analytics.personalCard.edit')}
            </button>
          )}
          {card.card !== null && card.card !== undefined && draft === null && (
            <CardBalanceForm
              key={card.card.card_id}
              merchantId={props.merchantId}
              cardId={card.card.card_id}
              idPrefix="personal"
            />
          )}
          {card.card !== null && card.card !== undefined && draft === null && (
            <CardBackgroundControl
              key={`${card.card.card_id}-${card.card.revision}`}
              merchantId={props.merchantId}
              cardId={card.card.card_id}
              revision={card.card.revision}
              imageUrl={card.card.background_image_url}
              idPrefix="personal"
              onChanged={() => void card.refetch()}
            />
          )}
          {feedback === 'success' && (
            <p role="status" className="mt-2 text-green-700">
              {t('merchant.analytics.personalCard.saved')}
            </p>
          )}
          {feedback !== null && feedback !== 'success' && (
            <p role="alert" className="mt-2 text-red-700">
              {t(
                feedback === 'conflict'
                  ? 'merchant.analytics.personalCard.conflict'
                  : feedback === 'invalid'
                    ? 'merchant.analytics.personalCard.invalid'
                    : 'merchant.analytics.personalCard.saveFailed',
              )}
            </p>
          )}
          {(feedback === 'conflict' || feedback === 'error') && (
            <button
              type="button"
              disabled={busy}
              className="mt-2 text-blue-600"
              onClick={() => void refresh()}
            >
              {t('merchant.analytics.personalCard.refresh')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
